import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SchoolLevel } from '@jeraisonne/db';
import { useCreateTheme } from '@/lib/themes/themes-queries';

export function TutorThemeNewPage() {
  const navigate = useNavigate();
  const create = useCreateTheme();
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [descriptionMd, setDescriptionMd] = useState('');
  const [minLevel, setMinLevel] = useState<SchoolLevel | ''>('');
  const [sensitivity, setSensitivity] = useState<'all' | 'sensitive' | 'mature'>('all');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        title,
        short_description: shortDescription,
        description_md: descriptionMd,
        min_level: minLevel || null,
        sensitivity,
      });
      navigate({ to: '/tutor/themes' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:py-8">
      <Link
        to="/tutor/themes"
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" /> Retour
      </Link>

      <h1 className="font-display text-ink-900 mt-3 text-[24px] font-bold">Nouveau thème</h1>
      <p className="text-ink-500 mt-1 font-sans text-[14px]">
        Visible par tes élèves liés. Soumets-le ensuite pour publication.
      </p>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        <Field label="Titre" required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={120}
            className="border-ink-200 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
          />
        </Field>

        <Field label="Description courte" hint="300 caractères max — sert au teaser SEO">
          <textarea
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            maxLength={300}
            rows={2}
            className="border-ink-200 focus:border-primary-500 w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
          />
        </Field>

        <Field label="Description longue (markdown)">
          <textarea
            value={descriptionMd}
            onChange={(e) => setDescriptionMd(e.target.value)}
            maxLength={20000}
            rows={8}
            className="border-ink-200 focus:border-primary-500 w-full rounded-xl border bg-white px-4 py-2 font-mono text-xs outline-none"
          />
        </Field>

        <Field label="Niveau scolaire minimum">
          <select
            value={minLevel}
            onChange={(e) => setMinLevel(e.target.value as SchoolLevel | '')}
            className="border-ink-200 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
          >
            <option value="">Tous niveaux</option>
            {SCHOOL_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Sensibilité">
          <select
            value={sensitivity}
            onChange={(e) => setSensitivity(e.target.value as 'all' | 'sensitive' | 'mature')}
            className="border-ink-200 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
          >
            <option value="all">Tout public</option>
            <option value="sensitive">Sujet sensible</option>
            <option value="mature">Adolescent / adulte</option>
          </select>
        </Field>

        {error && <p className="text-accent-600 text-sm">{error}</p>}

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Link
            to="/tutor/themes"
            className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border px-4 text-sm font-semibold"
          >
            Annuler
          </Link>
          <Button
            type="submit"
            disabled={create.isPending || !title.trim()}
            className="min-h-[2.75rem] w-full sm:w-auto"
          >
            {create.isPending ? 'Création…' : 'Créer le thème'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-display text-ink-800 text-[13px] font-semibold">
        {label}
        {required && <span className="text-accent-600 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="text-ink-400 text-[11px]">{hint}</span>}
    </label>
  );
}
